/**
 * Course-ban audit event schemas and Firebase paths.
 *
 * Every successful invocation of the `setCourseBan` callable
 * appends one of these to `/courseBanAudit/{courseId}/events/{eventId}`
 * so the durable trail of "who banned whom, when, and what was
 * lifted" survives even if a moderator later deletes the live ban doc.
 *
 * Per-course nesting (rather than the flat `/chatAudit/{eventId}`
 * shape) lets `firestore.rules` gate reads on
 * `isMinimumRole(courseId, "admin")` so a course's admins can see
 * only their own course's audit log.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/2865
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';
import {CourseBanDocumentSchema} from './user/courseBan';

/**
 * Action the admin took on the target user's ban doc.
 *
 * - `created` — no ban existed before; one was issued.
 * - `updated` — a ban already existed and was modified in place (e.g.
 *   scope changed, expiry extended, reason edited).
 * - `lifted` — the ban doc was deleted so the user regains access.
 */
export const COURSE_BAN_AUDIT_ACTIONS = [
  'created',
  'updated',
  'lifted',
] as const;

/** Action the admin took on the target user's ban doc. */
export const CourseBanAuditActionSchema = z.enum(COURSE_BAN_AUDIT_ACTIONS);

/** Action the admin took on the target user's ban doc. */
export type CourseBanAuditAction = z.infer<typeof CourseBanAuditActionSchema>;

/**
 * Audit event written every time `setCourseBan` mutates a ban doc.
 * Append-only: rules forbid updates or deletes so the trail can't be
 * tampered with after the fact.
 *
 * `.loose()` so adding new fields later (e.g. an admin's user-agent,
 * client IP region) doesn't break older readers.
 */
export const CourseBanAuditEventSchema = z
  .object({
    /** Action the admin took. */
    action: CourseBanAuditActionSchema,
    /**
     * The course the ban applies to. Redundant with the path segment
     * but stored as a field for collectionGroup queries.
     */
    courseId: z.string(),
    /** Uid of the user whose ban was created/updated/lifted. */
    targetUid: z.string(),
    /**
     * Uid of the admin who performed the action. Must match
     * `request.auth.uid` at write time (the callable re-resolves the
     * caller's role from Firestore before allowing the write).
     */
    enforcerUid: z.string(),
    /**
     * Snapshot of the ban doc before this event. `null` for `created`
     * actions (there was nothing before). For `updated` and `lifted`,
     * captures the prior state so an appeals reviewer can reconstruct
     * the timeline.
     */
    before: CourseBanDocumentSchema.nullable(),
    /**
     * Snapshot of the ban doc after this event. `null` for `lifted`
     * actions (the doc no longer exists). For `created` and `updated`,
     * captures the new state.
     */
    after: CourseBanDocumentSchema.nullable(),
    /** Server-assigned write time. */
    serverTimestamp: FirestoreTimestampSchema,
  })
  .loose();

/** A single course-ban audit event as stored in Firestore. */
export type CourseBanAuditEvent = z.infer<typeof CourseBanAuditEventSchema>;

/** Top-level audit collection — events nest under it by course. */
export const COURSE_BAN_AUDIT_COLLECTION_PATH = 'courseBanAudit' as const;

/**
 * Constructs the path to the `events` subcollection for a course's
 * audit log.
 *
 * Collection path:
 *   `courseBanAudit/{courseId}/events`
 *
 * @param courseId - The course whose audit subcollection is addressed.
 * @returns The fully-qualified Firestore collection path.
 */
export function getCourseBanAuditEventsCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${COURSE_BAN_AUDIT_COLLECTION_PATH}/${courseId}/events`;
}
